import React, {useEffect, useState} from 'react'
import Photo from '../../../../Camera/Photo/Photo'
import {
  BUTTON_TYPES,
  NavigationButtons
} from '../../../../Navigation/NavigationButtons/NavigatonButtons'
import FieldMessage from '../../../../Form/FieldMessage'
import {getVideoDevices} from '../../../../../utils/devices'
import ListSelect from '../../../../ListSelect/ListSelect'
import styled from 'styled-components'
import {_L, useLocaleState} from '../../../../../context/LocaleContext'
import {useUserState} from '../../../../../context/UserContext'

/**
 * Checks if the mediaDevices object is present. The mediaDevices object is not available in iOS when the
 * app is a Home Screen app.
 */
const hasMediaDevices = () => !!navigator.mediaDevices

const Document = ({setAction, setValue, parentState, visibleFields}) => {
  const [hasCamera, setHasCamera] = useState(false)
  const [videoDevices, setVideoDevices] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(null)
  const localeState = useLocaleState()
  const {user} = useUserState()
  const {isSubmitting, requester, requesterPhone, errors} = parentState
  const {hasDoc1, hasDoc2, hasDoc3, hasDoc4} = visibleFields
  const hasDocuments = hasDoc1 || hasDoc2 || hasDoc3 || hasDoc4
  const isMobile = window.matchMedia('(max-width:420px)')

  useEffect(() => {
    // Check if there is a camera available and that we are on a secure domain.
    if (
      (window.location.protocol === 'https:' || isMobile ||
        window.location.hostname === 'localhost') &&
      hasMediaDevices()
    ) {
      navigator.mediaDevices
        .getUserMedia({video: true})
        .then(() => {
          setHasCamera(true)
          return getVideoDevices()
        })
        .then(videoDevices => {
          const sources = videoDevices.map(device => ({
            label: device.label,
            value: device.deviceId
          }))
          setVideoDevices(sources)
          setSelectedDevice(sources[0].value)
        })
        .catch(() => setHasCamera(false))
    } else {
      // No camera, use File Input
      setHasCamera(false)
    }
  }, [])

  const onNext = evt => {	  
	    if(evt.key == "Enter" || evt.key == " " || !evt.key){ setAction({action: 'next'}) }
  }

  const onBack = evt => {	  
	    if(evt.key == "Enter" || evt.key == " " || !evt.key){ setAction({action: 'prev'}) }
  }

  const onPhoneChange = e => {

    setValue({requesterPhone: e.target.value})
  }

  const onPhotoAcquired = (doc, data) => setValue({[doc]: data})

  const onVideoDeviceChanged = device => setSelectedDevice(device.value)

  const hasMultipleDevices = videoDevices && videoDevices.length > 1

  return (
    <Container>
      <DocumentContent>
        {hasDocuments && (
          <TitleText>{_L({key: 'Upload Document or Photo', localeState})}</TitleText>
        )}
        <PhotoContainer>
          {hasDoc1 && (
            <Photo
              field="doc1"
              onPhotoAcquired={onPhotoAcquired}
              hasCamera={hasCamera}
              selectedDevice={selectedDevice}
              dataTestId="document-photo1"
            />
          )}
          {hasDoc2 && (
            <Photo
              field="doc2"
              onPhotoAcquired={onPhotoAcquired}
              hasCamera={hasCamera}
              selectedDevice={selectedDevice}
              dataTestId="document-photo2"
            />
          )}
          {hasDoc3 && (
            <Photo
              field="doc3"
              onPhotoAcquired={onPhotoAcquired}
              hasCamera={hasCamera}
              selectedDevice={selectedDevice}
              dataTestId="document-photo3"
            />
          )}
          {hasDoc4 && (
            <Photo
              field="doc4"
              onPhotoAcquired={onPhotoAcquired}
              hasCamera={hasCamera}
              selectedDevice={selectedDevice}
              dataTestId="document-photo4"
            />
          )}
        </PhotoContainer>

        {hasMultipleDevices && (
          <ListContainer>
            <StyledListSelect
              options={videoDevices}
              placeholder=""
              isSearchable={false}
              value={videoDevices.filter(({value}) => value === selectedDevice)}
              onChange={onVideoDeviceChanged}
            />
          </ListContainer>
        )}

        <FieldContainer>
          {hasDocuments && <Separator />}
          <StyledFieldLabel style={{display: 'none', paddingBottom: '18px'}}>
            <div>{_L({key: 'Requestor', localeState})}</div>
            <div style={{color: '#000000', paddingLeft: '12px'}}>
              {requester || user.employee.id}
            </div>
          </StyledFieldLabel>

          <StyledFieldLabel htmlFor="Requestor email">
            {_L({key: 'Requestor email', localeState})}
          </StyledFieldLabel>
          <StyledTextArea
          	id="Requestor email"
            rows={4}
            maxLength={24}
            onChange={onPhoneChange}
            value={requesterPhone}
            data-testid="document-phone"
          />
          <FieldMessage errors={errors} fieldName={'requesterPhone'} />
        </FieldContainer>
      </DocumentContent>
      <NavigationButtonContainer>
        <NavigationButtons
          onNextClick={onNext}
          nextButtonType={BUTTON_TYPES.SEND}
          onBackClick={onBack}
          loading={isSubmitting}
        />
      </NavigationButtonContainer>
    </Container>
  )
}

export default Document

const Container = styled.div`
  width: 600px;
  box-sizing: border-box;

  @media (max-width: 420px) {
    width: 100vw;
    overflow-y: scroll;
    height: 100%;
    background-color: white;
  }
`

const DocumentContent = styled.div`
  margin: 20px;
  border-radius: 12px;
  background-color: #ffffff;
  padding: 30px 30px 0 30px;
  box-sizing: border-box;
  box-shadow: 0 10px 40px 0 rgba(0, 0, 0, 0.05);
  overflow-y: auto;

  @media (max-width: 420px) {
    margin: 0;
    border-radius: 0;
    width: 100vw;
    height: 100%;
    padding: 6px 0 100px 0;
  }
`

const PhotoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 24px 0 24px;

  @media (max-width: 420px) {
    justify-content: space-around;
    padding: 10px 24px 0 24px;
  }
`

const NavigationButtonContainer = styled.div`
  @media (max-width: 420px) {
    width: 100vw;
    height: 70px;
    background-color: white;
    position: fixed;
    bottom: 0;
    padding-top: 6px;
    border-top: #13294b solid 2px;
  }
    
  }
`

const StyledTextArea = styled.textarea`
  width: 100%;
  border-radius: 6px;
  border: 0;
  height: 46px;
  background-color: #f3f5fb;
  padding: 12px 20px 10px 20px;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Open Sans';
  resize: none;
  :focus {
	outline: 3px solid #FF9900;
  }
	
  @media (max-width: 420px) {
    height: 70px;
  }
`

const ListContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 14px;
  box-sizing: border-box;
`

const StyledListSelect = styled(ListSelect)`
  min-width: 320px;
`

const TitleText = styled.div`
  padding: 14px 24px 14px 24px;
  font-size: 22px;
  font-weight: 700;
`

const Separator = styled.div`
  border-top: 1px solid #646669;
  padding: 0 24px 0 24px;
  margin: 17px 0;
`

const FieldContainer = styled.div`
  padding: 0 24px 44px 24px;

  @media (max-width: 420px) {
    padding: 10px 24px 0 24px;
    box-sizing: border-box;
  }
`

const StyledFieldLabel = styled.label`
  color: #3e4044;
  font-size: 14px;
  font-weight: normal;
  padding-bottom: 8px;
`
